import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Input } from '@arco-design/web-react';
import { IconPlus } from '@arco-design/web-react/icon';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { ImageSelectModal } from '@/components-biz/ImageSelect';

import {
  SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD,
  clearStoredContainerDeployConfig,
  writeStoredContainerDeployConfig,
} from '../../platform/container-deploy-config';
import { ResourceConfigCard } from '../resourceConfig';
import { buildImageResourceSummaryItems, resolveImageCardDisplayName } from './buildImageSummaryItems';
import type { ImageResourceSelectFieldProps } from './types';

/** 镜像卡片无计价字段时的占位前缀，仅满足 ResourceConfigCard 结构 */
const IMAGE_CARD_VENDOR_PREFIX = '__imageResourceCardPersist';

const IMAGE_CARD_ICON = 'icon-jingxiangmian';

/** 镜像选择：ResourceConfigCard 回填 + 弹窗选择 */
export function ImageResourceSelectField({
  form,
  queryObj = {},
  imageKeyField,
  imageUrlField,
  imagePrefix,
  variant = 'common',
  artifactType,
  lockArtifactType,
  readOnly = false,
  lockActionsWhenFilled = false,
  presetHasImage,
  summarySource,
  onImageChange,
  onImageClear,
}: ImageResourceSelectFieldProps) {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const selectedKey = Form.useWatch(imageKeyField, form) as string | undefined;
  const selectedUrl = Form.useWatch(imageUrlField, form) as string | undefined;
  const normalizedPrefix = imagePrefix?.replace(/\.$/, '');
  const watchedImageName = Form.useWatch(normalizedPrefix ? `${normalizedPrefix}.imageName` : undefined, form) as string | undefined;
  const watchedLegacyName = Form.useWatch(normalizedPrefix ? `${normalizedPrefix}.name` : undefined, form) as string | undefined;

  const hasImage = Boolean(String(selectedKey ?? '').trim() || String(selectedUrl ?? '').trim());
  /** 只读，或 SKU 预置已有镜像时锁删改（用户下单自选不锁） */
  const hideCardActions = readOnly || (lockActionsWhenFilled && presetHasImage === true);

  const summaryItems = useMemo(() => {
    if (summarySource) {
      return buildImageResourceSummaryItems(summarySource);
    }
    if (!hasImage) return [];
    const displayName = resolveImageCardDisplayName(form.getFieldsValue() as Record<string, unknown>, {
      imageKey: selectedKey,
      imageUrl: selectedUrl,
      imagePrefix,
    });
    return buildImageResourceSummaryItems({ displayName: displayName || '-' });
  }, [form, hasImage, imagePrefix, selectedKey, selectedUrl, summarySource, watchedImageName, watchedLegacyName]);

  const handleConfirm = useCallback(
    async (key: string, item: CommonImageItemType, imageUrl?: string) => {
      // 先写映射字段，再写 key/url，避免 summary 依赖的表单字段尚未就绪
      await onImageChange?.(key, item, imageUrl);
      form.setFieldValue(imageKeyField, key);
      form.setFieldValue(imageUrlField, imageUrl ?? item.name);
      writeStoredContainerDeployConfig(form, item.containerDeployConfig);
      setModalVisible(false);
    },
    [form, imageKeyField, imageUrlField, onImageChange],
  );

  const handleDelete = useCallback(() => {
    form.setFieldValue(imageKeyField, undefined);
    form.setFieldValue(imageUrlField, undefined);
    clearStoredContainerDeployConfig(form);
    onImageClear?.();
  }, [form, imageKeyField, imageUrlField, onImageClear]);

  const openModal = useCallback(() => {
    if (readOnly) return;
    setModalVisible(true);
  }, [readOnly]);

  return (
    <div>
      <Form.Item
        field={imageKeyField}
        label={t('common:image')}
        rules={
          readOnly
            ? undefined
            : [
                {
                  required: true,
                  message: t('common:validate.required.select', { name: t('common:image') }),
                },
              ]
        }
      >
        <Input style={{ display: 'none' }} />
        {hasImage ? (
          <ResourceConfigCard
            resourceCode="image"
            vendorDevicePrefix={IMAGE_CARD_VENDOR_PREFIX}
            icon={IMAGE_CARD_ICON}
            title={t('common:image')}
            items={summaryItems}
            canEdit={!hideCardActions}
            canDelete={!hideCardActions}
            onEdit={openModal}
            onDelete={handleDelete}
          />
        ) : readOnly ? null : (
          <Button type="dashed" long icon={<IconPlus />} onClick={openModal}>
            {t('common:image.select')}
          </Button>
        )}
      </Form.Item>
      <Form.Item field={SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD} hidden>
        <Input />
      </Form.Item>
      {modalVisible ? (
        <ImageSelectModal
          visible
          queryObj={queryObj}
          variant={variant}
          artifactType={artifactType}
          lockArtifactType={lockArtifactType}
          initialSelectedKey={selectedKey}
          onCancel={() => setModalVisible(false)}
          onConfirm={handleConfirm}
        />
      ) : null}
    </div>
  );
}
